import { zodResolver } from "@hookform/resolvers/zod";
import { type AxiosError } from "axios";
import { AnimatePresence, easeOut, motion } from "framer-motion";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useAddVoucherMutation } from "@services/vouchers/mutations";
import {
  useAddVoucherFormSchema,
  type TAddVoucherFormSchema,
} from "./useAddVoucherFormSchema";

interface IJointBillVoucherAddVoucherProps {
  isOpen: boolean;
  jointBillServiceId: number;
}

export const JointBillVoucherAddVoucher: React.FC<
  IJointBillVoucherAddVoucherProps
> = (props) => {
  const { isOpen, jointBillServiceId } = props;
  const { t } = useTranslation();

  const schema = useAddVoucherFormSchema();

  const { handleSubmit, control, setError } = useForm<TAddVoucherFormSchema>({
    resolver: zodResolver(schema),
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const { mutateAsync: addVoucherMutation, isPending } =
    useAddVoucherMutation();

  const addVoucher: SubmitHandler<TAddVoucherFormSchema> = async (data) => {
    await addVoucherMutation({
      jointBillServiceId: jointBillServiceId,
      voucherCode: data.voucherCode,
    })
      .then(() => {
        toast.success("bill.voucher.addVoucher.success");
      })
      .catch((error: AxiosError) => {
        setApiErrors(error, true);
      });
  };

  return (
    <div className="w-full">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -24, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -24, height: 0 }}
            transition={{ duration: 0.1, ease: easeOut }}
            className="mt-3 flex justify-end gap-4"
          >
            <form onSubmit={handleSubmit(addVoucher)} className="contents">
              <FormInput
                name="voucherCode"
                control={control}
                containerClassName="max-w-[20rem]"
                placeholder={t("bill.voucher.addVoucher.input.placeholder")}
                aria-label={t("bill.voucher.addVoucher.input.placeholder")}
              />
              <Button variant="secondary" isLoading={isPending}>
                {t("bill.voucher.addVoucher.submit")}
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
